import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import { useQuery } from "@apollo/client";
import { useStyles } from "./styles";
import {
  handleChangeRowsPerPage,
  handleChange,
  handleSubmit,
  handleCloseSnackbar,
  handleChangePage,
} from "./helpers";
import { GET_ALL_EXAM_PAPERS_ANSWER_KEY } from "utils/graphql/services/ai-evaluation/queries";
import { useHistory } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import FilterComponent from "shared/molecules/Filter";
import FilterInputs from "./filter/FilterInputs";
import autoGradeClient from "utils/graphql/autoGradeClient";
import { convertToIST } from "../common/Index";

type MuiAlertProps = {
  onClose: () => void;
  severity?: "error" | "info" | "success" | "warning" | undefined;
  children?: React.ReactNode;
};

const Alert = React.forwardRef<HTMLDivElement, MuiAlertProps>((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function AnswerKeyList() {
  const classes = useStyles();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [examPaperId, setExamPaperId] = useState("");
  const [isArchived, setIsArchived] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTill, setCreatedTill] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTill, setUpdatedTill] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    examPaperId: "",
    isArchived: "",
    createdFrom: "",
    createdTill: "",
    updatedFrom: "",
    updatedTill: "",
  });

  const {
    data,
    loading,
    error,
    refetch: gamesRefetch,
  } = useQuery(GET_ALL_EXAM_PAPERS_ANSWER_KEY, {
    variables: {
      input: {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        examPaperId: appliedFilters.examPaperId || undefined,
        isArchived: appliedFilters.isArchived
          ? appliedFilters.isArchived === "true"
          : undefined,
        createdFrom: appliedFilters.createdFrom || undefined,
        createdTill: appliedFilters.createdTill || undefined,
        updatedFrom: appliedFilters.updatedFrom || undefined,
        updatedTill: appliedFilters.updatedTill || undefined,
      },
    },
    client: autoGradeClient,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    gamesRefetch({
      input: {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        examPaperId: appliedFilters.examPaperId
          ? parseInt(appliedFilters.examPaperId)
          : undefined,
        isArchived: appliedFilters.isArchived
          ? appliedFilters.isArchived === "true"
          : undefined,
        createdFrom: appliedFilters.createdFrom || undefined,
        createdTill: appliedFilters.createdTill || undefined,
        updatedFrom: appliedFilters.updatedFrom || undefined,
        updatedTill: appliedFilters.updatedTill || undefined,
      },
    });
  }, [appliedFilters, page, rowsPerPage, gamesRefetch]);

  console.log("data", data?.getExamPaperAnswerKeys?.examPaperAnswerKeys);

  const answerKey = data?.getExamPaperAnswerKeys?.examPaperAnswerKeys || [];
  const totalCount =
    data?.getExamPaperAnswerKeys?.totalRecords ?? answerKey.length;
  const [isFilter, setIsFilter] = useState(false);
  const handleApplyFilter = () => {
    const filters = {
      examPaperId,
      isArchived,
      createdFrom,
      createdTill,
      updatedFrom,
      updatedTill,
    };
    setAppliedFilters(filters);
    setPage(0);
  };

  const handleClearFilter = () => {
    setExamPaperId("");
    setIsArchived("");
    setCreatedFrom("");
    setCreatedTill("");
    setUpdatedFrom("");
    setUpdatedTill("");

    setAppliedFilters({
      examPaperId: "",
      isArchived: "",
      createdFrom: "",
      createdTill: "",
      updatedFrom: "",
      updatedTill: "",
    });

    setPage(0);
  };

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <Paper elevation={3} className={classes.paper}>
          <Box display="flex" alignItems="center" justifyContent="end">
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/ai-evaluation/answer-key/create")}
            >
              Create Answer Key
            </Button>
          </Box>
          <Grid item mt={2}>
            <Box display="flex" justifyContent="flex-end">
              <FilterComponent isFilter={isFilter} setIsFilter={setIsFilter}>
                <div>
                  <FilterInputs
                    examPaperId={examPaperId}
                    setExamPaperId={setExamPaperId}
                    isArchived={isArchived}
                    setIsArchived={setIsArchived}
                    createdFrom={createdFrom}
                    setCreatedFrom={setCreatedFrom}
                    createdTill={createdTill}
                    setCreatedTill={setCreatedTill}
                    updatedFrom={updatedFrom}
                    setUpdatedFrom={setUpdatedFrom}
                    updatedTill={updatedTill}
                    setUpdatedTill={setUpdatedTill}
                    handleApplyFilter={handleApplyFilter}
                    handleClearFilter={handleClearFilter}
                  />
                </div>
              </FilterComponent>
            </Box>
          </Grid>
        </Paper>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Exam Paper Id</TableCell>
                  <TableCell>Is Archived</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {answerKey.map((answerKey) => (
                  <TableRow key={answerKey.id}>
                    <TableCell>{answerKey.id}</TableCell>
                    <TableCell>{answerKey.examPaperId}</TableCell>
                    <TableCell>{answerKey.isArchived ? "Yes" : "No"}</TableCell>
                    <TableCell>{answerKey.createdByName}</TableCell>
                    <TableCell>{answerKey.updatedByName}</TableCell>
                    <TableCell>{convertToIST(answerKey.createdAt)}</TableCell>
                    <TableCell>{convertToIST(answerKey.updatedAt)}</TableCell>
                    <TableCell>
                      <div>
                        {/* <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => history.push(`/ai-evaluation/answer-key/view/${answerKey.id}`)}
                    >
                      View
                    </Button> */}
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            history.push(
                              `/ai-evaluation/answer-key/update/${answerKey.id}`
                            )
                          }
                        >
                          Update
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) =>
                handleChangePage(e, newPage, setPage)
              }
              onRowsPerPageChange={(e: any) =>
                handleChangeRowsPerPage(e, setRowsPerPage, setPage)
              }
              rowsPerPageOptions={[]}
              component="div"
            />
          </TableContainer>
        )}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
      >
        <Alert
          onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
          severity={
            snackbar.severity as
              | "error"
              | "info"
              | "success"
              | "warning"
              | undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default AnswerKeyList;
